import React, { createContext, useContext, useReducer, useEffect } from 'react';

interface SubscriptionPlan {
  id: string;
  name: string;
  level: number;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  nickname: string;
  phone: string;
  zipCode: string;
  hasActiveSubscription: boolean;
  subscribedPlan: SubscriptionPlan;
  loggedIn: boolean;
};

interface Design {
  id?: string;
  name?: string;
  designName?: string;
  zone?: string;
  style?: string;
  sunlight?: string;
  windows?: string;
  facade?: string;
  houseSize?: number;
  shape?: string;
  bedSize?: string;
  bedConfig?: number;
  chosenPlants?: string[];
}

interface AppState {
  user: User;
  designs: Design[];
  currentDesign: Design | null;
  userId: string;
  designName: string;
  zone: string | null;
  style: string | null;
  sunlight: string | null;
  windows: string | null;
  facade: string | null;
  houseSize: number | null;
  shape: string | null;
  bedSize: string | null;
  bedConfig: number | null;
  chosenPlants: any[];
  filteredList?: any[];
  isMobile: boolean;
  isTablet: boolean;
}

interface AppAction {
  type: string;
  payload?: any;
}

const subscriptionPlanOptions: SubscriptionPlan[] = [
  // TODO: Revert default level to 0 after Free Trail level defined
  { id: '1', name: 'Free Trial', level: 1 },
  { id: '3158a507-9faa-4c7d-a4a3-2720c397003c', name: 'Basic', level: 1 },
  { id: 'baa997e8-c033-44d2-baad-71944a01db2f', name: 'Premium', level: 2 },
];

const getSubscriptionPlanById = (id: string) => {
  return subscriptionPlanOptions.find(subscription => subscription.id === id) ?? subscriptionPlanOptions[0];
};

const initialState = {
  user: {
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    nickname: '',
    phone: '',
    zipCode: '',
    hasActiveSubscription: false,
    subscribedPlan: subscriptionPlanOptions[0],
    loggedIn: false,
  },
  designs: [],
  currentDesign: {},
  userId: '',
  designName: '',
  zone: null,
  style: null,
  sunlight: null,
  windows: null,
  facade: null,
  houseSize: null,
  shape: null,
  bedSize: null,
  bedConfig: null,
  chosenPlants: [],
  isMobile: false,
  isTablet: false,
};

const designData = {
  userId: '',
  designId: '',
  designName: '',
  zone: '',
  style: '',
  sunlight: '',
  windows: '',
  facade: '',
  houseSize: 0,
  shape: '',
  bedSize: '',
  bedConfig: 0,
  chosenPlants: [],
};

const SET_USER_ID = 'SET_USER_ID';
const SET_USER = 'SET_USER';
const SET_HAS_ACTIVE_SUBSCRIPTION = 'SET_HAS_ACTIVE_SUBSCRIPTION';
const SET_SUBSCRIBED_PLAN = 'SET_SUBSCRIBED_PLAN';
const SET_CURRENT_DESIGN = 'SET_CURRENT_DESIGN';
const SET_DESIGN_NAME = 'SET_DESIGN_NAME';
const SET_ZONE = 'SET_ZONE';
const SET_STYLE = 'SET_STYLE';
const SET_SUNLIGHT = 'SET_SUNLIGHT';
const SET_WINDOWS = 'SET_WINDOW';
const SET_FACADE = 'SET_FACADE';
const SET_HOUSE_SIZE = 'SET_HOUSE_SIZE';
const SET_SHAPE = 'SET_SHAPE';
const SET_BED_SIZE = 'SET_BED_SIZE';
const SET_BED_CONFIG = 'SET_BED_CONFIG';
const SET_FILTERED_LIST = 'SET_FILTERED_LIST';
const SET_CHOSEN_PLANTS = 'SET_CHOSEN_PLANTS';
const ADD_OR_UPDATE_PLANT = 'ADD_OR_UPDATE_PLANT ';
const SET_DESIGNS = 'SET_DESIGNS';
const RESET_STATE = 'RESET_STATE';
const SET_IS_MOBILE = 'SET_IS_MOBILE';
const SET_IS_TABLET = 'SET_IS_TABLET';

const reducer = (state: AppState, action: AppAction): AppState => {
  console.log('reducer action:', action);
  switch (action.type) {
    case SET_USER_ID:
      return { ...state, user: { ...state.user, id: action.payload } };
    case SET_USER:
      return { ...state, user: action.payload };
    case SET_HAS_ACTIVE_SUBSCRIPTION:
      return { ...state, user: { ...state.user, hasActiveSubscription: action.payload } };
    case SET_SUBSCRIBED_PLAN:
      // Payload is subscription id
      return { ...state, user: { ...state.user, subscribedPlan: getSubscriptionPlanById(action.payload) } };
    case SET_CURRENT_DESIGN:
      return { ...state, currentDesign: action.payload };
    case SET_DESIGN_NAME:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), designName: action.payload } };
    case SET_ZONE:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), zone: action.payload } };
    case SET_STYLE:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), style: action.payload } };
    case SET_SUNLIGHT:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), sunlight: action.payload } };
    case SET_WINDOWS:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), windows: action.payload } };
    case SET_FACADE:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), facade: action.payload } };
    case SET_HOUSE_SIZE:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), houseSize: action.payload } };
    case SET_SHAPE:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), shape: action.payload } };
    case SET_BED_SIZE:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), bedSize: action.payload } };
    case SET_BED_CONFIG:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), bedConfig: action.payload } };
    case SET_CHOSEN_PLANTS:
      return { ...state, currentDesign: { ...(state.currentDesign || {}), chosenPlants: action.payload } };
    case ADD_OR_UPDATE_PLANT: {
      const { category, plantId, quantity } = action.payload;
      const updatedPlants = (state.currentDesign?.chosenPlants || []).map((plant: any) =>
        plant.category === category
          ? { ...plant, plantId, quantity: plant.quantity || quantity }
          : plant
      );
      return { ...state, currentDesign: { ...(state.currentDesign || {}), chosenPlants: updatedPlants } };
    }
    case SET_FILTERED_LIST:
      return { ...state, filteredList: action.payload };
    case SET_DESIGNS:
      return { ...state, designs: action.payload };
    case RESET_STATE:
      return { ...initialState };
    case SET_IS_MOBILE:
      return { ...state, isMobile: action.payload };
    case SET_IS_TABLET:
      return { ...state, isTablet: action.payload };
    default:
      return state;
  }
};

const AppContext = createContext<{ state: AppState; dispatch: React.Dispatch<AppAction> } | undefined>(undefined);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);

export {
  SET_USER_ID,
  SET_USER,
  SET_HAS_ACTIVE_SUBSCRIPTION,
  SET_SUBSCRIBED_PLAN,
  SET_CURRENT_DESIGN,
  SET_DESIGN_NAME,
  SET_ZONE,
  SET_STYLE,
  SET_SUNLIGHT,
  SET_WINDOWS,
  SET_FACADE,
  SET_HOUSE_SIZE,
  SET_SHAPE,
  SET_BED_SIZE,
  SET_BED_CONFIG,
  SET_FILTERED_LIST,
  SET_CHOSEN_PLANTS,
  ADD_OR_UPDATE_PLANT,
  SET_DESIGNS,
  RESET_STATE,
  SET_IS_MOBILE,
  SET_IS_TABLET,
  AppContext,
};
